"use client";

import type { CSSProperties } from "react";

import { useNoteLabel } from "@/hooks/use-notation";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { EASE_IN, EASE_OUT, FADE, STAT_STAGGER_MS, ease, pick } from "../motion";
import {
  FONT_MONO,
  TEXT,
  TEXT_MUTE,
} from "@/components/ui/organisms/chord-practice/palette";
import type {
  PieceTessitura,
  PieceTonality,
} from "@/lib/piece-overview/piece-overview-data";

interface PieceStatsProps {
  tonality: PieceTonality | null;
  timeSignature: string;
  bpm: number;
  tessitura: PieceTessitura | null;
  /**
   * Les quatre s'en vont : la fiche se retourne sur ses parcours.
   *
   * Le bloc tient son extinction lui-même au lieu de la laisser à la boîte qui
   * lui rend sa place, parce qu'il a quatre parties à échelonner et qu'une
   * opacité posée sur l'ensemble les aplatirait toutes les quatre.
   */
  leaving: boolean;
}

/**
 * Centre block of the overlay : four "respirations" highlighting the
 * **musical identity** of the piece, tonality, time signature, tempo,
 * tessitura. These are the keys (no pun intended) the user wants up
 * front to know what they're about to play. Numerical content stats
 * (notes, density, duration) live in the eyebrow at the top right.
 *
 * No cards, no borders : the values float above the aurora gradient.
 */
export function PieceStats({
  tonality,
  timeSignature,
  bpm,
  tessitura,
  leaving,
}: PieceStatsProps) {
  const toNoteLabel = useNoteLabel();
  const reduced = useReducedMotion();

  /*
    Elles s'éteignent de droite à gauche, donc la tonalité part en dernier.

    Ce n'est pas une coquetterie : elle est la seule des quatre à survivre, en
    se reposant à côté du compositeur, et les deux temps se recouvrent. L'oeil
    voit alors une donnée changer de place, et non deux évènements.

    Au retour, les quatre reviennent ensemble : un repli n'a rien à raconter.
  */
  const base = pick(FADE.stats, leaving);
  const curve = leaving ? EASE_IN : EASE_OUT;

  const timing = (rank: number): CSSProperties => {
    const beat = leaving
      ? { ...base, delay: base.delay + (STATS - 1 - rank) * STAT_STAGGER_MS }
      : base;

    return {
      opacity: leaving ? 0 : 1,
      transform: leaving && !reduced ? "translateY(-6px)" : "none",
      transition: reduced
        ? "none"
        : [ease("opacity", beat, curve), ease("transform", beat, curve)].join(", "),
    };
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 56,
        flexWrap: "wrap",
      }}
    >
      <Stat label="Tonalité" value={formatTonality(tonality, toNoteLabel)} fade={timing(0)} />
      <Stat label="Mesure" value={timeSignature} fade={timing(1)} />
      <Stat label="Tempo" value={`♩ = ${bpm}`} fade={timing(2)} />
      <Stat
        label="Tessiture"
        value={formatTessitura(tessitura, toNoteLabel)}
        fade={timing(3)}
      />
    </div>
  );
}

/** Combien il y en a. Le rang de chacune décide de son tour de sortie. */
const STATS = 4;

function Stat({
  label,
  value,
  fade,
}: {
  label: string;
  value: string;
  fade: CSSProperties;
}) {
  return (
    <div style={{ textAlign: "center", ...fade }}>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 26,
          fontWeight: 300,
          color: TEXT,
          letterSpacing: "-0.01em",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          color: TEXT_MUTE,
          fontFamily: FONT_MONO,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function formatTonality(
  t: PieceTonality | null,
  toNoteLabel: (name: string) => string,
): string {
  if (!t) {
    return ", ";
  }

  const modeLabel = t.mode === "major" ? "maj" : "min";

  return `${toNoteLabel(t.tonic)} ${modeLabel}`;
}

function formatTessitura(
  t: PieceTessitura | null,
  toNoteLabel: (name: string) => string,
): string {
  if (!t) {
    return ", ";
  }

  return `${toNoteLabel(t.lowest)} → ${toNoteLabel(t.highest)}`;
}
